import { useEffect, useState } from "react";
import {
  CardBody,
  Form,
  Row,
  Col,
  Button,
  Table,
  Spinner,
} from "react-bootstrap";
import Swal from "sweetalert2";
import { useForm } from "react-hook-form";
import { useDataFetch } from "../../../hooks/use-data-fetch";
import {
  useCountries,
  useProvinces,
  useCities,
} from "../../../hooks/use-locations";
import NavList from "../NavList";

const ITEMS_PER_PAGE = 5;

const AddressSettingsForm = () => {
  const { getData, invalidateQuery, postData, patchData } = useDataFetch();

  const { mutateAsync: postAddress } = postData("/address");
  const { mutateAsync: updateAddress } = patchData("/address");
  const { mutateAsync: disableAddress } = patchData("/address/disable");

  const { data, isLoading: isLoadingAddresses } = getData(["address"], "/address");

  const [currentPage, setCurrentPage] = useState(1);
  const [editingAddressId, setEditingAddressId] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm();

  const addresses = data?.data?.addresses || [];
  const totalItems = addresses.length;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const currentAddresses = addresses.slice(
    startIndex,
    startIndex + ITEMS_PER_PAGE
  );

  const countryId = watch("country_id");
  const provinceId = watch("province_id");
  const receiverType = watch("receiver_type", "self");

  const { countries } = useCountries();
  const { provinces } = useProvinces(countryId);
  const { cities } = useCities(provinceId);

  const onSubmit = async (formData: any) => {
    const {
      receiver_type,
      receiver_name,
      receiver_document,
      receiver_phone,
      ...rest
    } = formData;

    const payload = {
      ...rest,
      address: formData.address.trim(),
      receiver: {
        type: receiver_type,
        name: receiver_name || null,
        document: receiver_document || null,
        phone: receiver_phone || null,
      },
    };

    try {
      if (editingAddressId) {
        await updateAddress({ id: editingAddressId, ...payload });
      } else {
        await postAddress(payload);
      }

      invalidateQuery(["address"]);
      handleCancelEdit();
    } catch (error) {
      console.error(error);
    }
  };

  const handleEditAddress = (address: any) => {
    setEditingAddressId(address.id);

    reset({
      name: address.name || "",
      receiver_type: address.receiver?.type || "self",
      receiver_name: address.receiver?.name || "",
      receiver_document: address.receiver?.document || "",
      receiver_phone: address.receiver?.phone || "",

      address: address.address || "",
      country_id: address.country_id?.id || "",
      province_id: address.province_id?.id || "",
      city_id: address.city_id?.id || "",
      postal_code: address.postal_code || "",
      observations: address.observations || "",
    });
  };

  const handleCancelEdit = () => {
    setEditingAddressId(null);
    reset({
      receiver_type: "self",
      name: "",
      province_id: "",
      city_id: "",
      postal_code: "",
      observations: "",
      address: "",
      receiver_name: "",
      receiver_document: "",
      receiver_phone: "",
    });
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleDeleteAddress = async (id: string) => {
    const result = await Swal.fire({
      title: "Eliminar dirección",
      text: "¿Estás seguro de que deseas eliminar esta dirección?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#dc3545",
    });

    if (!result.isConfirmed) return;

    try {
      await disableAddress({ id });

      Swal.fire({
        icon: "success",
        title: "¡Eliminada!",
        text: "La dirección ha sido eliminada correctamente.",
        confirmButtonText: "Entendido",
      });

      invalidateQuery(["address"]);

    } catch (error: any) {
      console.error("Error eliminando dirección:", error);

      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error?.response?.data?.message ||
          "Ocurrió un error al eliminar la dirección.",
        confirmButtonText: "Entendido",
      });
    }
  };

  useEffect(() => {
    if (countries?.length && !editingAddressId) {
      const colombia = countries.find(
        (c: any) => c.name?.toLowerCase() === "colombia"
      );

      if (colombia) {
        reset((prev: any) => ({
          ...prev,
          country_id: colombia.id,
          receiver_type: "self"
        }));
      }
    }
  }, [countries, editingAddressId, reset]);

  return (
    <div className="bg-white p-4 rounded-3 mb-4 position-relative">
      <NavList />
      <CardBody>
        <Form onSubmit={handleSubmit(onSubmit)} className="mb-4">
          <Row>
            <Col md={12}>
              <Form.Group className="mb-4">
                <Form.Label className="fw-semibold">
                  ¿Quién recibe el pedido?
                </Form.Label>

                <div className="d-flex gap-4 mt-2">
                  <Form.Check
                    type="radio"
                    label="Yo recibiré el pedido"
                    value="self"
                    {...register("receiver_type")}
                  />

                  <Form.Check
                    type="radio"
                    label="Otra persona recibirá el pedido"
                    value="other"
                    {...register("receiver_type")}
                  />
                </div>
              </Form.Group>
            </Col>
          </Row>

          {receiverType === "other" && (
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Nombre completo</Form.Label>
                  <Form.Control
                    placeholder="Ej. María Pérez"
                    {...register("receiver_name", {
                      required: receiverType === "other",
                    })}
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Número de documento</Form.Label>
                  <Form.Control
                    placeholder="Ej. 1020304050"
                    {...register("receiver_document", {
                      required: receiverType === "other",
                    })}
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Teléfono de contacto</Form.Label>
                  <Form.Control
                    placeholder="Ej. 300 123 4567"
                    {...register("receiver_phone", {
                      required: receiverType === "other",
                    })}
                  />
                </Form.Group>
              </Col>
            </Row>
          )}
          <Row>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Alias dirección</Form.Label>
                <Form.Control
                  placeholder="Ej. Casa, Trabajo"
                  maxLength={15}
                  {...register("name", { required: true, maxLength: 15 })}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Dirección</Form.Label>
                <Form.Control
                  placeholder="Ej. Cra. 34 # 11B - 70"
                  maxLength={50}
                  isInvalid={!!errors.address}
                  {...register("address", { required: true, maxLength: 50 })}
                />
                <Form.Text muted>
                  {(watch("address") || "").length} / 50 caracteres
                </Form.Text>
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Código postal</Form.Label>
                <Form.Control
                  placeholder="Ej. 050021"
                  maxLength={10}
                  {...register("postal_code", {
                    maxLength: 10,
                    pattern: /^[0-9]+$/,
                  })}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>País</Form.Label>
                <Form.Select {...register("country_id", { required: true })} style={{ height: "50px" }}>
                  <option value="">Selecciona</option>
                  {countries.map((c: any) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Departamento / Provincia</Form.Label>
                <Form.Select {...register("province_id", { required: true })} style={{ height: "50px" }}>
                  <option value="">Selecciona</option>
                  {provinces.map((p: any) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Ciudad</Form.Label>
                <Form.Select {...register("city_id", { required: true })} style={{ height: "50px" }}>
                  <option value="">Selecciona</option>
                  {cities.map((c: any) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>Observaciones</Form.Label>
                <Form.Control
                  as="textarea" rows={3}
                  placeholder="Ej. Frente al supermercado, portón negro"
                  {...register("observations")}
                />
              </Form.Group>
            </Col>
          </Row>

          {/* PREVIEW */}
          <div
            className="mb-3 p-3 rounded-3 border"
            style={{
              border: "1px solid #e2e8f0",
            }}
          >
            <div style={{ fontSize: "13px", color: "#fff" }}>
              Vista previa de dirección
            </div>

            <div style={{ fontWeight: 500, marginTop: "5px" }}>
              {`${watch("address") || ""} ${watch("observations") || ""}`}
            </div>

            <div style={{ fontSize: "14px", color: "#fff", marginTop: "4px" }}>
              {countries.find((c: any) => c.id === watch("country_id"))?.name || ""}{" "}
              - {provinces.find((p: any) => p.id === watch("province_id"))?.name || ""}{" "}
              - {cities.find((c: any) => c.id === watch("city_id"))?.name || ""}
            </div>
          </div>

          <div className="d-flex gap-2">
            <Button type="submit">
              Guardar
            </Button>
          </div>
        </Form>

        {isLoadingAddresses ? (
          <Spinner animation="border" />
        ) : (
          <div className="default-table-area ec-recent-orders">
            <div className="table-responsive">
              <Table className="align-middle">
                <thead>
                  <tr>
                    <th>Alias</th>
                    <th>Dirección</th>
                    <th>Recibe</th>
                    <th>Nombre receptor</th>
                    <th>Documento</th>
                    <th>Teléfono</th>
                    <th>Ciudad</th>
                    <th>Provincia</th>
                    <th>País</th>
                    <th>Código Postal</th>
                    <th>Observaciones</th>
                    <th>Acciones</th>
                  </tr>
                </thead>

                <tbody>
                  {currentAddresses.length > 0 ? (
                    currentAddresses.map((addr: any) => (
                      <tr key={addr.id}>
                        <td>{addr.name}</td>

                        <td
                          style={{
                            maxWidth: "250px",
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                          }}
                        >
                          {addr.address}
                        </td>

                        <td>
                          {addr.receiver?.type === "other"
                            ? "Otra persona"
                            : "Titular"}
                        </td>

                        <td>{addr.receiver?.name || "-"}</td>

                        <td>{addr.receiver?.document || "-"}</td>

                        <td>{addr.receiver?.phone || "-"}</td>

                        <td>{addr.city_id?.name || "-"}</td>

                        <td>{addr.province_id?.name || "-"}</td>

                        <td>{addr.country_id?.name || "-"}</td>

                        <td>{addr.postal_code || "-"}</td>

                        <td
                          style={{
                            maxWidth: "200px",
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                          }}
                        >
                          {addr.observations || "-"}
                        </td>

                        <td>
                          <div className="d-flex gap-2">

                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleEditAddress(addr)}
                              disabled={editingAddressId === addr.id}
                            >
                              <i className="ri-edit-line"></i>
                            </Button>

                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDeleteAddress(addr.id)}
                            >
                              <i className="ri-delete-bin-line"></i>
                            </Button>

                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={12} className="text-center">
                        No hay direcciones registradas.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            <div className="d-flex justify-content-center justify-content-sm-between align-items-center text-center flex-wrap gap-2 showing-wrap">
              <span className="fs-13 fw-medium">
                Mostrando {startIndex + 1} a{" "}
                {Math.min(startIndex + ITEMS_PER_PAGE, totalItems)} de{" "}
                {totalItems} resultados
              </span>
              <nav aria-label="Paginación">
                <ul className="pagination mb-0 justify-content-center">
                  <li
                    className={`page-item ${currentPage === 1 ? "disabled" : ""
                      }`}
                  >
                    <button
                      className="page-link icon"
                      onClick={() => handlePageChange(currentPage - 1)}
                    >
                      <span className="material-symbols-outlined">
                        keyboard_arrow_left
                      </span>
                    </button>
                  </li>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                    (page) => (
                      <li key={page} className="page-item">
                        <button
                          className={`page-link ${currentPage === page ? "active" : ""
                            }`}
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </button>
                      </li>
                    )
                  )}
                  <li
                    className={`page-item ${currentPage === totalPages ? "disabled" : ""
                      }`}
                  >
                    <button
                      className="page-link icon"
                      onClick={() => handlePageChange(currentPage + 1)}
                    >
                      <span className="material-symbols-outlined">
                        keyboard_arrow_right
                      </span>
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        )}
      </CardBody>
    </div>
  );
};

export default AddressSettingsForm;
